import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import api from "../services/api";
import AnniversaireTableAssignment from "./AnniversaireTableAssignment";
import "./AnniversaireList.css";

interface Anniversaire {
  _id: string;
  personne: string;
  age: number;
  province: string;
  dateISO: string;
  heureISO: string;
  lieu: string;
  image: string;
  rsvp: boolean;
  theme: {
    background: string;
    text: string;
    accent: string;
    button: string;
  };
  createdAt: string;
}

interface Invite {
  _id: string;
  nom: string;
  telephone: string;
  table?: string;
  statut: string;
  lienEnvoye: boolean;
  uniqueToken: string;
  dateConfirmation?: string;
}

interface ApiResponse {
  success: boolean;
  anniversaires?: Anniversaire[];
  anniversaire?: Anniversaire;
  invites?: Invite[];
  tables?: string[];
  assignments?: Record<string, string>;
  protocoles?: any[];
  message?: string;
  error?: string;
  count?: number;
  skipped?: number;
}

interface Props {
  onSelectAnniversaire?: (id: string) => void;
  readOnly?: boolean;
  canDelete?: boolean;
  filterProvince?: string;
}

const AnniversaireList = ({ onSelectAnniversaire, readOnly = false, canDelete = true, filterProvince }: Props) => {
  const [anniversaires, setAnniversaires] = useState<Anniversaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedAnniversaire, setExpandedAnniversaire] = useState<string | null>(null);
  const [invitesMap, setInvitesMap] = useState<Record<string, Invite[]>>({});
  const [loadingInvites, setLoadingInvites] = useState<Record<string, boolean>>({});
  const [tablesMap, setTablesMap] = useState<Record<string, string[]>>({});
  const [assignmentsMap, setAssignmentsMap] = useState<Record<string, Record<string, string>>>({});
  const [protocolesMap, setProtocolesMap] = useState<Record<string, any[]>>({});
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAnniversaire, setSelectedAnniversaire] = useState<Anniversaire | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<Anniversaire>>({});
  const [saving, setSaving] = useState(false);
  
  const [newInviteNom, setNewInviteNom] = useState("");
  const [newInviteTelephone, setNewInviteTelephone] = useState("");
  const [newInviteTable, setNewInviteTable] = useState("");
  const [addingInvite, setAddingInvite] = useState(false);
  const [importing, setImporting] = useState(false);
  
  const [activeTab, setActiveTab] = useState<"invites" | "tables">("invites");

  useEffect(() => {
    fetchAnniversaires();
  }, []);

  const getDateAnniversaire = (anniversaire: Anniversaire): string => {
    const date = new Date(anniversaire.dateISO);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const fetchAnniversaires = async () => {
    try {
      setLoading(true);
      const response = await api.get<ApiResponse>("/anniversaires");
      if (response.data.success) {
        setAnniversaires(response.data.anniversaires || []);
      }
    } catch (error) {
      console.error("Erreur chargement anniversaires:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvites = async (anniversaireId: string) => {
    if (invitesMap[anniversaireId]) return;
    
    setLoadingInvites(prev => ({ ...prev, [anniversaireId]: true }));
    try {
      const [invitesRes, tablesRes, assignmentsRes, protocolesRes] = await Promise.all([
        api.get<ApiResponse>(`/anniversaires/${anniversaireId}/invites`),
        api.get<ApiResponse>(`/anniversaires/${anniversaireId}/tables`),
        api.get<ApiResponse>(`/anniversaires/${anniversaireId}/assignments`),
        api.get<ApiResponse>(`/anniversaires/${anniversaireId}/protocoles-disponibles`)
      ]);
      
      if (invitesRes.data.success) {
        setInvitesMap(prev => ({ ...prev, [anniversaireId]: invitesRes.data.invites || [] }));
      }
      
      if (tablesRes.data.success) {
        setTablesMap(prev => ({ ...prev, [anniversaireId]: tablesRes.data.tables || [] }));
      }
      
      if (assignmentsRes.data.success) {
        setAssignmentsMap(prev => ({ ...prev, [anniversaireId]: assignmentsRes.data.assignments || {} }));
      }
      
      if (protocolesRes.data.success) {
        setProtocolesMap(prev => ({ ...prev, [anniversaireId]: protocolesRes.data.protocoles || [] }));
      }
      
    } catch (error) {
      console.error("Erreur chargement données:", error);
    } finally {
      setLoadingInvites(prev => ({ ...prev, [anniversaireId]: false }));
    }
  };

  const toggleExpand = (anniversaireId: string) => {
    if (expandedAnniversaire === anniversaireId) {
      setExpandedAnniversaire(null);
      setActiveTab("invites");
    } else {
      setExpandedAnniversaire(anniversaireId);
      setActiveTab("invites");
      fetchInvites(anniversaireId);
    }
  };

  const addInvite = async (anniversaireId: string) => {
    if (!newInviteNom.trim()) {
      alert("Veuillez entrer un nom");
      return;
    }
    setAddingInvite(true);
    try {
      const response = await api.post<ApiResponse>(`/anniversaires/${anniversaireId}/invites`, {
        nom: newInviteNom,
        telephone: newInviteTelephone,
        table: newInviteTable
      });
      if (response.data.success) {
        alert("Invité ajouté avec succès");
        setNewInviteNom("");
        setNewInviteTelephone("");
        setNewInviteTable("");
        fetchInvites(anniversaireId);
      } else {
        alert(response.data.message || "Erreur lors de l'ajout");
      }
    } catch (error: any) {
      console.error("Erreur:", error);
      alert(error.response?.data?.message || "Erreur lors de l'ajout");
    } finally {
      setAddingInvite(false);
    }
  };

  const readExcelFile = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length > 0) {
            const headers = jsonData[0] as string[];
            const rows = jsonData.slice(1);
            
            const formattedData = rows.map((row: any) => {
              const obj: any = {};
              headers.forEach((header, index) => {
                if (header && header.trim() !== "") {
                  obj[header.trim()] = row[index] || "";
                }
              });
              return obj;
            }).filter(row => Object.keys(row).length > 0);
            
            resolve(formattedData);
          } else {
            resolve([]);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = (error) => reject(error);
      reader.readAsBinaryString(file);
    });
  };

  const readCSVFile = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter(line => line.trim() !== '');
          
          if (lines.length > 0) {
            let separator = '\t';
            if (lines[0].includes(',')) separator = ',';
            if (lines[0].includes(';')) separator = ';';
            
            const headers = lines[0].split(separator).map(h => h.trim());
            const rows = lines.slice(1);
            
            const formattedData = rows.map((line) => {
              const values = line.split(separator);
              const obj: any = {};
              headers.forEach((header, index) => {
                if (header && header.trim() !== "") {
                  obj[header.trim()] = values[index] ? values[index].trim() : "";
                }
              });
              return obj;
            }).filter(row => Object.keys(row).length > 0);
            
            resolve(formattedData);
          } else {
            resolve([]);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = (error) => reject(error);
      reader.readAsText(file, 'UTF-8');
    });
  };

  const downloadExcelTemplate = (anniversaire: Anniversaire) => {
    const wb = XLSX.utils.book_new();
    
    const wsData = [
      ["Nom", "Téléphone", "Table"]
    ];
    
    for (let i = 0; i < 20; i++) {
      wsData.push(["", "", ""]);
    }
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    const wscols = [
      { wch: 30 },
      { wch: 20 },
      { wch: 15 }
    ];
    ws['!cols'] = wscols;
    
    XLSX.utils.book_append_sheet(wb, ws, "Invités");
    
    const fileName = `invites-${anniversaire.personne}-anniversaire.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const exportInvitesToExcel = (anniversaire: Anniversaire, invites: Invite[]) => {
    const wb = XLSX.utils.book_new();
    
    const wsData = [
      ["Nom", "Téléphone", "Table", "Statut", "Date de confirmation", "Lien d'invitation"]
    ];
    
    invites.forEach(invite => {
      wsData.push([
        invite.nom,
        invite.telephone || "-",
        invite.table || "-",
        invite.statut === "confirmed" ? "Confirmé" : 
        invite.statut === "declined" ? "Décliné" : "En attente",
        invite.dateConfirmation ? new Date(invite.dateConfirmation).toLocaleString('fr-FR') : "-",
        `${window.location.origin}/anniversaire/${invite.uniqueToken}`
      ]);
    });
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    const wscols = [
      { wch: 30 },
      { wch: 20 },
      { wch: 15 },
      { wch: 12 },
      { wch: 20 },
      { wch: 50 }
    ];
    ws['!cols'] = wscols;
    
    XLSX.utils.book_append_sheet(wb, ws, "Invités");
    
    const fileName = `invites-${anniversaire.personne}-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>, anniversaireId: string) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(csv|xlsx|xls)$/i)) {
      alert("Veuillez sélectionner un fichier Excel (.csv, .xlsx, .xls)");
      return;
    }

    handleImportInvites(anniversaireId, file);
  };

  const handleImportInvites = async (anniversaireId: string, file: File) => {
    setImporting(true);

    try {
      let guestsData: any[] = [];
      
      if (file.name.endsWith('.csv')) {
        guestsData = await readCSVFile(file);
      } else if (file.name.match(/\.(xlsx|xls)$/i)) {
        guestsData = await readExcelFile(file);
      } else {
        alert("Format de fichier non supporté");
        return;
      }

      if (guestsData.length === 0) {
        alert("Aucune donnée trouvée dans le fichier");
        return;
      }

      const normalizedData = guestsData.map((guest) => {
        const nom = guest.Nom || guest.NOM || guest.nom || guest['Nom complet'] || guest.Name || '';
        const telephone = guest.Téléphone || guest.TELEPHONE || guest.telephone || 
                         guest.Tél || guest.tel || guest['Numéro téléphone'] || guest.Phone || '';
        const table = guest.Table || guest.TABLE || guest.table || '';

        let normalizedPhone = telephone.toString().trim();
        normalizedPhone = normalizedPhone.replace(/\s+/g, '');
        normalizedPhone = normalizedPhone.replace(/[^\d+]/g, '');
        
        if (normalizedPhone && !normalizedPhone.startsWith('+')) {
          normalizedPhone = '+243' + normalizedPhone;
        }

        return {
          nom: nom.toString().trim(),
          telephone: normalizedPhone,
          table: table.toString().trim()
        };
      }).filter(guest => guest.nom !== "");

      if (normalizedData.length === 0) {
        alert("Aucune donnée valide après traitement");
        return;
      }

      const response = await api.post<ApiResponse>(`/anniversaires/${anniversaireId}/invites/batch`, {
        invites: normalizedData.map(guest => ({
          nom: guest.nom,
          telephone: guest.telephone,
          table: guest.table,
          statut: "pending"
        }))
      });

      if (response.data.success) {
        alert(`${normalizedData.length} invités créés avec succès !`);
        fetchInvites(anniversaireId);
      } else {
        alert(`Erreur: ${response.data.message || "Impossible de créer les invitations"}`);
      }

    } catch (error: any) {
      console.error("Erreur:", error);
      alert(error.response?.data?.message || "Une erreur est survenue lors de la lecture du fichier");
    } finally {
      setImporting(false);
    }
  };

  const deleteInvite = async (anniversaireId: string, inviteId: string) => {
    if (!window.confirm("Supprimer cet invité ?")) return;
    try {
      const response = await api.delete<ApiResponse>(`/anniversaires/${anniversaireId}/invites/${inviteId}`);
      if (response.data.success) {
        fetchInvites(anniversaireId);
      }
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la suppression");
    }
  };

  const copyInviteLink = (token: string) => {
    const link = `${window.location.origin}/anniversaire/${token}`;
    navigator.clipboard.writeText(link);
    alert("✅ Lien copié !");
  };

  const shareWhatsApp = (invite: Invite, anniversaire: Anniversaire) => {
    if (!invite.telephone) {
      alert("📱 Numéro de téléphone non renseigné");
      return;
    }
    
    const cleanPhone = invite.telephone.replace(/\s+/g, '').replace(/^\+/, '');
    const link = `${window.location.origin}/anniversaire/${invite.uniqueToken}`;
    const dateFormatted = getDateAnniversaire(anniversaire);
    const age = anniversaire.age;
    const nomPersonne = anniversaire.personne;
    
    const message = `🎂 *INVITATION ANNIVERSAIRE* 🎂

*Bonjour ${invite.nom},*

C'est avec une grande joie que nous vous invitons à célébrer l'anniversaire de *${nomPersonne}* qui fête ses *${age} ans* !

*📅 Date :* ${dateFormatted}
*⏰ Heure :* ${anniversaire.heureISO}

*🔗 Lien d'invitation :* ${link}

*✅ Merci de confirmer votre présence en cliquant sur le lien ci-dessus.*

Nous serions ravis de vous compter parmi nous pour célébrer ce moment spécial !

💝 Cordialement,
La famille`;
    
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet anniversaire ?")) {
      return;
    }
    try {
      const response = await api.delete<ApiResponse>(`/anniversaires/${id}`);
      if (response.data.success) {
        alert("Anniversaire supprimé avec succès");
        fetchAnniversaires();
      } else {
        alert(response.data.message || "Erreur lors de la suppression");
      }
    } catch (error: any) {
      console.error("Erreur suppression:", error);
      alert(error.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const openEditModal = (anniversaire: Anniversaire) => {
    setSelectedAnniversaire(anniversaire);
    setEditFormData(anniversaire);
    setShowEditModal(true);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: checked }));
  };

  const saveEdit = async () => {
    if (!selectedAnniversaire) return;
    setSaving(true);
    try {
      const response = await api.put<ApiResponse>(`/anniversaires/${selectedAnniversaire._id}`, editFormData);
      if (response.data.success) {
        alert("Anniversaire modifié avec succès");
        setShowEditModal(false);
        fetchAnniversaires();
      } else {
        alert(response.data.message || "Erreur lors de la modification");
      }
    } catch (error: any) {
      console.error("Erreur:", error);
      alert(error.response?.data?.message || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Chargement des anniversaires...</div>;
  }

  return (
    <div className="anniversaire-list-container">
      <div className="list-header">
        <h2>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
          </svg>
          Liste des anniversaires
        </h2>
      </div>

      <div className="anniversaire-stats">
        <div className="stat-card">
          <span className="stat-value">{anniversaires.length}</span>
          <span className="stat-label">Total anniversaires</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {anniversaires.filter(a => a.age >= 60).length}
          </span>
          <span className="stat-label">Séniors (60+)</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {anniversaires.filter(a => a.age < 18 && a.age > 0).length}
          </span>
          <span className="stat-label">Enfants</span>
        </div>
      </div>

      {anniversaires.length === 0 ? (
        <p className="empty-message">Aucun anniversaire trouvé</p>
      ) : (
        <div className="table-container">
          <table className="anniversaire-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Âge</th>
                <th>Date</th>
                <th>Lieu</th>
                <th>RSVP</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {anniversaires.map(anniv => (
                <React.Fragment key={anniv._id}>
                  <tr className={expandedAnniversaire === anniv._id ? "expanded-row" : ""}>
                    <td>{anniv.personne}</td>
                    <td>{anniv.age} ans</td>
                    <td>{new Date(anniv.dateISO).toLocaleDateString('fr-FR')}</td>
                    <td>{anniv.lieu || "-"}</td>
                    <td>
                      {anniv.rsvp ? (
                        <span className="rsvp-badge active">Activé</span>
                      ) : (
                        <span className="rsvp-badge inactive">Désactivé</span>
                      )}
                    </td>
                    <td className="actions-cell">
                      <button className="btn-action" title="Voir les invités" onClick={() => toggleExpand(anniv._id)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-1 .05 1.16.84 2 1.87 2 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                        </svg>
                      </button>
                      <button className="btn-action edit" title="Modifier" onClick={() => openEditModal(anniv)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                        </svg>
                      </button>
                      <button className="btn-action delete" title="Supprimer" onClick={() => handleDelete(anniv._id)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                        </svg>
                      </button>
                      {onSelectAnniversaire && (
                        <button
                          className="assign-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectAnniversaire(anniv._id);
                          }}
                          title="Assigner des tables à des protocoles"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H4V5h16v14zM8 10h8v2H8v-2z" fill="currentColor"/>
                          </svg>
                          Assigner tables
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedAnniversaire === anniv._id && (
                    <tr className="invites-row">
                      <td colSpan={6}>
                        <div className="invites-container">
                          <div className="invites-header">
                            <h4>Gestion de l'anniversaire</h4>
                            <div className="tab-buttons">
                              <button 
                                className={`tab-btn ${activeTab === "invites" ? "active" : ""}`}
                                onClick={() => setActiveTab("invites")}
                              >
                                Invités ({invitesMap[anniv._id]?.length || 0})
                              </button>
                              <button 
                                className={`tab-btn ${activeTab === "tables" ? "active" : ""}`}
                                onClick={() => setActiveTab("tables")}
                              >
                                Tables ({tablesMap[anniv._id]?.length || 0})
                              </button>
                            </div>
                          </div>
                          
                          {activeTab === "invites" && (
                            <>
                              <div className="invites-actions-bar">
                                <button 
                                  className="template-btn"
                                  onClick={() => downloadExcelTemplate(anniv)}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/>
                                  </svg>
                                  Télécharger modèle
                                </button>
                                <button 
                                  className="export-btn"
                                  onClick={() => exportInvitesToExcel(anniv, invitesMap[anniv._id] || [])}
                                  disabled={!invitesMap[anniv._id]?.length}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/>
                                  </svg>
                                  Exporter Excel
                                </button>
                                <input 
                                  type="file" 
                                  id={`excel-upload-${anniv._id}`}
                                  accept=".csv,.xlsx,.xls"
                                  style={{ display: 'none' }}
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      handleExcelUpload(e, anniv._id);
                                    }
                                  }}
                                />
                                <button 
                                  className="upload-btn"
                                  onClick={() => document.getElementById(`excel-upload-${anniv._id}`)?.click()}
                                  disabled={importing}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" fill="currentColor"/>
                                  </svg>
                                  Importer Excel
                                </button>
                              </div>
                              
                              <div className="add-invite-form">
                                <input 
                                  type="text" 
                                  placeholder="Nom complet *" 
                                  value={newInviteNom} 
                                  onChange={(e) => setNewInviteNom(e.target.value)} 
                                />
                                <input 
                                  type="tel" 
                                  placeholder="Téléphone (WhatsApp)" 
                                  value={newInviteTelephone} 
                                  onChange={(e) => setNewInviteTelephone(e.target.value)} 
                                />
                                <input 
                                  type="text" 
                                  placeholder="Table (optionnel)" 
                                  value={newInviteTable} 
                                  onChange={(e) => setNewInviteTable(e.target.value)} 
                                  list={`tables-list-${anniv._id}`}
                                />
                                <datalist id={`tables-list-${anniv._id}`}>
                                  {tablesMap[anniv._id]?.map(table => (
                                    <option key={table} value={table} />
                                  ))}
                                </datalist>
                                <button 
                                  className="add-invite-btn" 
                                  onClick={() => addInvite(anniv._id)} 
                                  disabled={addingInvite}
                                >
                                  {addingInvite ? "Ajout..." : "+ Ajouter"}
                                </button>
                              </div>
                              
                              {loadingInvites[anniv._id] ? (
                                <div className="loading-spinner"><div className="spinner"></div></div>
                              ) : !invitesMap[anniv._id]?.length ? (
                                <p className="empty-invites">Aucun invité pour cet anniversaire</p>
                              ) : (
                                <table className="invites-table">
                                  <thead>
                                    <tr>
                                      <th>Nom</th>
                                      <th>Téléphone</th>
                                      <th>Table</th>
                                      <th>Statut</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {invitesMap[anniv._id].map((invite: Invite) => (
                                      <tr key={invite._id}>
                                        <td>{invite.nom}</td>
                                        <td>{invite.telephone || "-"}</td>
                                        <td>{invite.table || "-"}</td>
                                        <td>
                                          <span className={`status-badge ${invite.statut}`}>
                                            {invite.statut === "confirmed" ? "Confirmé" : 
                                             invite.statut === "declined" ? "Décliné" : "En attente"}
                                          </span>
                                        </td>
                                        <td className="actions-cell">
                                          <button 
                                            className="action-btn copy" 
                                            onClick={() => copyInviteLink(invite.uniqueToken)} 
                                            title="Copier le lien"
                                          >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor"/>
                                            </svg>
                                          </button>
                                          {invite.telephone && (
                                            <button 
                                              className="action-btn whatsapp" 
                                              onClick={() => shareWhatsApp(invite, anniv)} 
                                              title="Envoyer sur WhatsApp"
                                            >
                                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M19.05 4.91C17.18 3.24 14.81 2.2 12.2 2.04 5.95 1.56 2.2 6.38 2.2 9.4c0 1.4.5 2.7 1.3 3.8L3 18.9l5.8-1.6c1.2.7 2.5 1.1 3.9 1.1 5.3 0 9.1-4.8 9.1-9.1 0-2.4-1-4.7-2.8-6.4z" fill="currentColor"/>
                                              </svg>
                                            </button>
                                          )}
                                          <button 
                                            className="action-btn delete" 
                                            onClick={() => deleteInvite(anniv._id, invite._id)} 
                                            title="Supprimer"
                                          >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                                            </svg>
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </>
                          )}
                          
                          {activeTab === "tables" && (
                            <AnniversaireTableAssignment 
                              anniversaireId={anniv._id}
                              tables={tablesMap[anniv._id] || []}
                              assignments={assignmentsMap[anniv._id] || {}}
                              protocoles={protocolesMap[anniv._id] || []}
                              onUpdate={() => fetchInvites(anniv._id)}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modale de modification */}
      {showEditModal && selectedAnniversaire && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content modal-edit" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Modifier l'anniversaire</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); saveEdit(); }}>
                <div className="form-group">
                  <label>Nom de la personne</label>
                  <input type="text" name="personne" value={editFormData.personne || ""} onChange={handleEditChange} />
                </div>
                
                <div className="form-group">
                  <label>Âge</label>
                  <input type="number" name="age" value={editFormData.age || ""} onChange={handleEditChange} />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Date</label>
                    <input type="date" name="dateISO" value={editFormData.dateISO || ""} onChange={handleEditChange} />
                  </div>
                  <div className="form-group">
                    <label>Heure</label>
                    <input type="time" name="heureISO" value={editFormData.heureISO || ""} onChange={handleEditChange} />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Lieu</label>
                  <input type="text" name="lieu" value={editFormData.lieu || ""} onChange={handleEditChange} />
                </div>
                
                <div className="form-group">
                  <label>Province</label>
                  <input type="text" name="province" value={editFormData.province || ""} onChange={handleEditChange} />
                </div>
                
                <div className="form-group checkbox-group">
                  <label>
                    <input type="checkbox" name="rsvp" checked={editFormData.rsvp || false} onChange={handleCheckboxChange} />
                    Activer RSVP
                  </label>
                </div>
                
                <div className="modal-actions">
                  <button type="submit" className="save-btn" disabled={saving}>
                    {saving ? "Enregistrement..." : "Enregistrer"}
                  </button>
                  <button type="button" className="cancel-btn" onClick={() => setShowEditModal(false)}>
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnniversaireList;